/**
 * Photo tiles for the Design brief rail.
 *
 *   MatchedPhotoTile     — the matched product reference image (+ trust badge).
 *   ArrangementPhotoTile — the designer's own captured photo (poll + delete).
 *
 * Previously a fixed side-by-side pair. Split apart so the matched photo can sit
 * beside the recipe (the two build references) while the arrangement photo —
 * empty until the build is finished — moves below out of prime space.
 */
import React, { useState } from 'react';
import { Camera, X, Image as ImageIcon, Trash2 } from 'lucide-react';
import { useArrangementPhoto } from '@/hooks/useArrangementPhoto';
import ArrangementPhotoStatus from '@/components/ArrangementPhotoStatus';
import { offlineMediaLabel } from '@/utils/mediaPresentation';
import { productReferenceMediaLabel } from '@/utils/productReference';
import TrustBadge from '@/components/design/TrustBadge';

function Lightbox({ url, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="pair-lightbox"
    >
      <button className="absolute top-4 right-4 text-white/70 hover:text-white" onClick={onClose} aria-label="Close">
        <X className="h-6 w-6" />
      </button>
      <img
        src={url}
        alt="Full size"
        className="max-w-[90vw] max-h-[90vh] object-contain rounded shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

export function MatchedPhotoTile({
  imageUrl,
  trustState,
  label = 'Matched Photo',
  referenceImage = null,
}) {
  const [lightbox, setLightbox] = useState(false);
  return (
    <>
      <span className="block text-sm font-bold text-stone-600 uppercase tracking-wider mb-1">{label}</span>
      <div className="relative rounded-lg border border-stone-200 bg-stone-50 overflow-hidden aspect-[3/4]">
        {imageUrl && trustState && <div className="absolute top-1.5 left-1.5 z-10"><TrustBadge state={trustState} /></div>}
        {imageUrl ? (
          <button type="button" onClick={() => setLightbox(true)} className="w-full h-full" data-testid="pair-matched-thumb">
            <img src={imageUrl} alt="Matched product reference" className="w-full h-full object-cover" />
          </button>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-center px-2" data-testid="pair-matched-empty">
            <ImageIcon className="h-5 w-5 text-stone-300" />
            <span
              className="text-[9px] font-medium text-stone-400 mt-1 leading-tight"
              data-testid={referenceImage?.configured
                ? 'pair-matched-unavailable'
                : 'pair-matched-empty'}
            >
              {productReferenceMediaLabel(referenceImage, 'no matched product photo')}
            </span>
          </div>
        )}
      </div>
      {lightbox && imageUrl && <Lightbox url={imageUrl} onClose={() => setLightbox(false)} />}
    </>
  );
}

export function ArrangementPhotoTile({ order, assignedDesignerId }) {
  const workflow = useArrangementPhoto(order?.id, order?.arrangement_photo, { assignedDesignerId });
  const {
    photo,
    imageResult,
    imageUrl,
    busy,
    canDelete,
    remove,
  } = workflow;
  const [lightbox, setLightbox] = useState(false);

  const handleDelete = () => {
    if (!canDelete || busy) return;
    const displayedPhoto = photo;
    if (!window.confirm('Delete this arrangement photo?')) return;
    void remove(displayedPhoto);
  };

  return (
    <>
      <span className="block text-sm font-bold text-stone-600 uppercase tracking-wider mb-1">Arrangement Photo</span>
      {/* Capped width so a portrait photo doesn't balloon across the full row.
          The rest of this row is reserved for future per-order-type content. */}
      <div className="relative rounded-lg border border-stone-200 bg-stone-50 overflow-hidden aspect-[3/4] w-full max-w-[200px]">
        {imageUrl ? (
          <>
            <button type="button" onClick={() => setLightbox(true)} className="w-full h-full" data-testid="pair-arrangement-thumb">
              <img src={imageUrl} alt="Arrangement" className="w-full h-full object-cover" />
            </button>
          </>
        ) : photo ? (
          imageResult?.state === 'loading' ? (
            <div
              className="w-full h-full animate-pulse bg-stone-200"
              data-testid="pair-arrangement-loading"
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center text-center px-2 text-[9px] font-medium text-stone-400 leading-tight"
              data-testid="pair-arrangement-unavailable"
            >
              {offlineMediaLabel(imageResult)}
            </div>
          )
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-center px-2" data-testid="pair-arrangement-waiting">
            <Camera className="h-5 w-5 text-stone-300" />
            <span className="text-[9px] font-medium text-stone-400 mt-1 leading-tight">waiting for phone</span>
          </div>
        )}
        {photo && canDelete && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={busy}
            className="absolute bottom-1 right-1 z-10 rounded-full bg-white/85 p-1 text-red-500 hover:text-red-700 disabled:opacity-40"
            data-testid="pair-arrangement-delete"
            title="Delete photo"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <ArrangementPhotoStatus workflow={workflow} />
      {lightbox && imageUrl && <Lightbox url={imageUrl} onClose={() => setLightbox(false)} />}
    </>
  );
}
